// Verified Store program — application submission, admin review, and public directory.
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const TierEnum = z.enum(["verified_business", "verified_store"]);

export type VerificationTier = z.infer<typeof TierEnum>;
export type VerificationStatus = "pending" | "needs_info" | "approved" | "rejected";

export type StoreVerificationApplication = {
  id: string;
  user_id: string;
  requested_tier: VerificationTier;
  granted_tier: VerificationTier | null;
  status: VerificationStatus;
  business_name: string;
  business_registration_number: string;
  business_email: string;
  business_website: string;
  store_city: string | null;
  store_province: string | null;
  id_document_path: string | null;
  proof_of_address_path: string | null;
  store_photo_paths: string[];
  admin_message: string | null;
  created_at: string;
  reviewed_at: string | null;
  purge_after: string | null;
  applicant_username?: string | null;
};

// ---------------- Submit ----------------
const SubmitSchema = z
  .object({
    requested_tier: TierEnum,
    business_name: z.string().trim().min(2).max(120),
    business_registration_number: z.string().trim().min(2).max(80),
    business_email: z.string().trim().email().max(254),
    business_website: z.string().trim().min(4).max(2048),
    store_city: z.string().trim().max(80).optional().nullable(),
    store_province: z.string().trim().max(80).optional().nullable(),
    id_document_path: z.string().trim().max(512).optional().nullable(),
    proof_of_address_path: z.string().trim().max(512).optional().nullable(),
    store_photo_paths: z.array(z.string().max(512)).max(10).optional(),
    declaration_accepted: z.literal(true),
  })
  .superRefine((v, ctx) => {
    if (v.requested_tier === "verified_store") {
      if (!v.id_document_path)
        ctx.addIssue({ code: "custom", path: ["id_document_path"], message: "ID document required" });
      if (!v.proof_of_address_path)
        ctx.addIssue({ code: "custom", path: ["proof_of_address_path"], message: "Proof of address required" });
      if (!v.store_city || !v.store_province)
        ctx.addIssue({ code: "custom", path: ["store_city"], message: "Store city & province required" });
    }
  });

function ensureOwnedPath(p: string | null | undefined, userId: string) {
  if (!p) return;
  const first = p.split("/")[0];
  if (first !== userId) throw new Error("Document path ownership mismatch");
}

export const submitStoreVerification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => SubmitSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // No pending application already
    const { data: existing } = await supabase
      .from("store_verification_applications")
      .select("id, status")
      .eq("user_id", userId)
      .in("status", ["pending", "needs_info"])
      .maybeSingle();
    if (existing) throw new Error("You already have an application in review.");

    ensureOwnedPath(data.id_document_path, userId);
    ensureOwnedPath(data.proof_of_address_path, userId);
    for (const p of data.store_photo_paths ?? []) ensureOwnedPath(p, userId);

    const { error } = await supabase.from("store_verification_applications").insert({
      user_id: userId,
      requested_tier: data.requested_tier,
      business_name: data.business_name,
      business_registration_number: data.business_registration_number,
      business_email: data.business_email,
      business_website: data.business_website,
      store_city: data.store_city ?? null,
      store_province: data.store_province ?? null,
      id_document_path: data.id_document_path ?? null,
      proof_of_address_path: data.proof_of_address_path ?? null,
      store_photo_paths: data.store_photo_paths ?? [],
      declaration_accepted_at: new Date().toISOString(),
      status: "pending",
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------- My application status ----------------
export const getMyStoreVerification = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("store_verification_applications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { application: (data as StoreVerificationApplication | null) ?? null };
  });

// ---------------- Storage helpers (uploads via signed URLs handled client-side with regular session) ----------------
const SignedUploadSchema = z.object({ path: z.string().min(1).max(512) });

export const createVerificationUploadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => SignedUploadSchema.parse(i))
  .handler(async ({ data, context }) => {
    ensureOwnedPath(data.path, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await supabaseAdmin.storage
      .from("store-verification-docs")
      .createSignedUploadUrl(data.path);
    if (error || !signed) throw new Error(error?.message ?? "Could not create upload URL");
    return { url: signed.signedUrl, token: signed.token, path: data.path };
  });

// ---------------- Admin: list ----------------
async function ensureAdmin(supabase: ReturnType<typeof createClient<Database>>, userId: string) {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Admin access required");
}

export const listVerificationApplications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase as any, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: apps, error } = await supabaseAdmin
      .from("store_verification_applications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    const userIds = Array.from(new Set((apps ?? []).map((a) => a.user_id)));
    const { data: profiles } = await supabaseAdmin
      .from("profiles")
      .select("id, username")
      .in("id", userIds);
    const nameById = new Map((profiles ?? []).map((p) => [p.id, p.username]));
    return {
      applications: (apps ?? []).map((a) => ({
        ...a,
        applicant_username: nameById.get(a.user_id) ?? null,
      })) as StoreVerificationApplication[],
    };
  });

// ---------------- Admin: signed URL for review ----------------
const ReviewUrlSchema = z.object({ application_id: z.string().uuid(), path: z.string().min(1) });
export const getVerificationDocUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => ReviewUrlSchema.parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as any, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: app, error } = await supabaseAdmin
      .from("store_verification_applications")
      .select("id, id_document_path, proof_of_address_path, store_photo_paths")
      .eq("id", data.application_id)
      .maybeSingle();
    if (error || !app) throw new Error(error?.message ?? "Application not found");
    const allowed =
      data.path === app.id_document_path ||
      data.path === app.proof_of_address_path ||
      (app.store_photo_paths ?? []).includes(data.path);
    if (!allowed) throw new Error("Path not associated with this application");
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from("store-verification-docs")
      .createSignedUrl(data.path, 60 * 15);
    if (sErr || !signed) throw new Error(sErr?.message ?? "Could not sign URL");
    return { url: signed.signedUrl };
  });

// ---------------- Admin: approve / reject / request info ----------------
const ReviewSchema = z.object({
  application_id: z.string().uuid(),
  decision: z.enum(["approve", "reject", "needs_info"]),
  granted_tier: TierEnum.optional(),
  message: z.string().max(2000).optional(),
});

const PURGE_DAYS = 90;

export const reviewStoreVerification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => ReviewSchema.parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as any, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: app, error: aErr } = await supabaseAdmin
      .from("store_verification_applications")
      .select("*")
      .eq("id", data.application_id)
      .maybeSingle();
    if (aErr || !app) throw new Error(aErr?.message ?? "Application not found");

    const now = new Date();
    const purgeAfter = new Date(now.getTime() + PURGE_DAYS * 24 * 60 * 60 * 1000).toISOString();

    if (data.decision === "needs_info") {
      const { error } = await supabaseAdmin
        .from("store_verification_applications")
        .update({
          status: "needs_info",
          admin_message: data.message ?? null,
          reviewed_by: context.userId,
          reviewed_at: now.toISOString(),
        })
        .eq("id", app.id);
      if (error) throw new Error(error.message);
      await supabaseAdmin.from("notifications").insert({
        user_id: app.user_id,
        type: "verification_update", title: "Verification: more info needed",
        body: data.message ?? "Please update your store verification application.",
        link: "/seller-verification",
      });
      return { ok: true };
    }

    if (data.decision === "reject") {
      const { error } = await supabaseAdmin
        .from("store_verification_applications")
        .update({
          status: "rejected",
          admin_message: data.message ?? null,
          reviewed_by: context.userId,
          reviewed_at: now.toISOString(),
          purge_after: purgeAfter,
        })
        .eq("id", app.id);
      if (error) throw new Error(error.message);
      await supabaseAdmin.from("notifications").insert({
        user_id: app.user_id,
        type: "verification_update", title: "Verification application rejected",
        body: data.message ?? "See your verification page for details.",
        link: "/seller-verification",
      });
      return { ok: true };
    }

    // approve
    const tier = data.granted_tier ?? app.requested_tier;
    const { error: pErr } = await supabaseAdmin
      .from("profiles")
      .update({
        verified_business: true,
        verified_store: tier === "verified_store",
        verified_since: now.toISOString(),
        store_name: app.business_name,
        store_city: app.store_city ?? null,
        store_province: app.store_province ?? null,
      })
      .eq("id", app.user_id);
    if (pErr) throw new Error(pErr.message);

    const { error } = await supabaseAdmin
      .from("store_verification_applications")
      .update({
        status: "approved",
        granted_tier: tier,
        admin_message: data.message ?? null,
        reviewed_by: context.userId,
        reviewed_at: now.toISOString(),
        purge_after: purgeAfter,
      })
      .eq("id", app.id);
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("notifications").insert({
      type: "verification_update",
      user_id: app.user_id,
      title:
        tier === "verified_store"
          ? "You're now a Verified Store!"
          : "You're now a Verified Business!",
      body: "Your verification badge is live across the platform.",
      link: "/seller-verification",
    });

    return { ok: true, tier };
  });

// ---------------- Public: Verified Stores directory ----------------
const DirectorySchema = z
  .object({
    tcg: z.string().max(40).optional(),
    location: z.string().max(80).optional(),
  })
  .optional();

export type VerifiedStoreRow = {
  id: string;
  username: string;
  store_name: string | null;
  store_city: string | null;
  store_province: string | null;
  tcg_specialisations: string[] | null;
  seller_rating_avg: number | null;
  seller_rating_count: number | null;
  seller_completed_count: number | null;
  verified_business: boolean;
  verified_store: boolean;
};

export const listVerifiedStores = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => (DirectorySchema.parse(i) ?? {}))
  .handler(async ({ data }) => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    let q = supa
      .from("profiles")
      .select(
        "id, username, store_name, store_city, store_province, tcg_specialisations, seller_rating_avg, seller_rating_count, seller_completed_count, verified_business, verified_store",
      )
      .eq("verified_store", true)
      .order("seller_rating_avg", { ascending: false, nullsFirst: false })
      .limit(200);
    if (data?.location) q = q.ilike("store_city", `%${data.location}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    let out = (rows ?? []) as VerifiedStoreRow[];
    if (data?.tcg) {
      const t = data.tcg.toLowerCase();
      out = out.filter((r) => (r.tcg_specialisations ?? []).some((x) => x.toLowerCase().includes(t)));
    }
    return { stores: out };
  });

// ---------------- Public: seller flags lookup (browse "verified only" filter) ----------------
const FlagsSchema = z.object({ ids: z.array(z.string().uuid()).max(200) });

export const getSellerVerificationFlags = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => FlagsSchema.parse(i))
  .handler(async ({ data }) => {
    if (data.ids.length === 0) return { flags: {} as Record<string, { verified_business: boolean; verified_store: boolean; trusted_seller: boolean }> };
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: rows, error } = await supa
      .from("profiles")
      .select("id, verified_business, verified_store, trusted_seller")
      .in("id", data.ids);
    if (error) throw new Error(error.message);
    const flags: Record<string, { verified_business: boolean; verified_store: boolean; trusted_seller: boolean }> = {};
    for (const r of rows ?? []) {
      flags[r.id] = {
        verified_business: !!r.verified_business,
        verified_store: !!r.verified_store,
        trusted_seller: !!r.trusted_seller,
      };
    }
    return { flags };
  });
